/**
 * Socket / some REST payloads may use PascalCase or put body in alternate fields.
 * Use these helpers so incoming replies (and all messages) map consistently.
 */

import { getFileTypeByLink } from 'utils/helpers';

export function getMessageNumericIdFromPayload(m: Record<string, unknown>): number | undefined {
  const raw = m.id ?? m.Id;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) return Number.parseInt(raw.trim(), 10);
  return undefined;
}

/** Primary text body — reply-type messages sometimes use `text` / `body` instead of `message`. */
export function getMessageBodyFromPayload(m: Record<string, unknown>): string {
  const msgDirect = m.message ?? m.Message;
  if (typeof msgDirect === 'string' && msgDirect.trim()) return msgDirect;

  const textRaw = m.text ?? m.Text;
  if (typeof textRaw === 'string' && textRaw.trim()) return textRaw;
  /* WhatsApp Cloud: text: { body: "..." } */
  if (textRaw && typeof textRaw === 'object' && !Array.isArray(textRaw)) {
    const body = (textRaw as Record<string, unknown>).body ?? (textRaw as Record<string, unknown>).Body;
    if (typeof body === 'string') return body;
  }

  const v =
    m.body ??
    m.Body ??
    m.displayMessage ??
    m.DisplayMessage ??
    m.formattedMessage ??
    m.FormattedMessage ??
    m.caption ??
    m.Caption;
  return typeof v === 'string' ? v : '';
}

/**
 * סוג המדיה לתצוגה. `reply` אינו סוג מדיה ב-WhatsApp (כמו שלא שולחים messageType reply ל-API) —
 * מנרמלים ל-text או מסיקים מ-fileUrl כשיש מדיה.
 */
export function getMessageMediaTypeFromPayload(m: Record<string, unknown>): string {
  const messageTypeRaw = m.messageType ?? m.MessageType;
  if (typeof messageTypeRaw === 'string' && messageTypeRaw.trim().toLowerCase() === 'reaction') {
    return 'reaction';
  }

  const fileTypeRaw = m.fileType ?? m.FileType;
  if (typeof fileTypeRaw === 'string' && fileTypeRaw.trim()) {
    const ft = fileTypeRaw.trim();
    if (ft.toLowerCase() === 'reaction') return 'reaction';
    return ft;
  }

  const v = m.type ?? m.Type;
  if (typeof v !== 'string' || !v.trim()) return '';
  const raw = v.trim();
  if (raw.toLowerCase() === 'reaction') return 'reaction';
  if (raw.toLowerCase() === 'reply') {
    const fu = m.fileUrl ?? m.FileUrl;
    if (typeof fu === 'string' && fu.trim()) {
      const inferred = getFileTypeByLink(fu);
      if (inferred && inferred !== '') return inferred;
    }
    return 'text';
  }
  return raw;
}

/**
 * מזהה הודעה ב-WhatsApp (wamid) — נדרש ל־`replyMessageId` ב־SendOutgoingMessage כשמגיבים להודעה.
 * לא לבלבל עם מזהה מספרי פנימי ב-DB.
 */
export function getWhatsappMessageIdFromPayload(m: Record<string, unknown>): string | undefined {
  const explicit =
    m.whatsappMessageId ??
    m.WhatsappMessageId ??
    m.wamid ??
    m.Wamid ??
    m.whatsappId ??
    m.WhatsappId;
  if (typeof explicit === 'string' && explicit.trim()) return explicit.trim();

  const mid = m.messageId ?? m.MessageId;
  if (typeof mid !== 'string' || !mid.trim()) return undefined;
  const t = mid.trim();
  // wamid או מזהה לא-רק-ספרות (לא Id פנימי)
  if (t.includes('wamid') || /[a-zA-Z_]/.test(t)) return t;
  return undefined;
}
